/**
 * TimelineItem Repository
 *
 * Manages workflow instance timeline items (steps, signals, errors, transitions)
 * FERP SAFETY: High criticality - timeline provides audit trail for life-critical workflows
 */

import { BaseRepository } from './BaseRepository';
import { TimelineItem } from '../types/WorkflowEntities';

class TimelineItemRepository extends BaseRepository<TimelineItem> {
    protected entityName = 'timelineItem';
    protected graphQLType = 'TimelineItem';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<TimelineItem | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'TimelineItem',
            criticalityLevel: 'HIGH', // Timeline items are critical audit trail
            metadata: { id, timelineItemRepository: true }
        });

        const query = `
            query GetTimelineItemByTenantAndId($id: String!, $tenant: String!) {
                timelineItem(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ timelineItem: TimelineItem | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 5 * 60 * 1000 // 5 minutes cache for timeline items
            });

            const timelineItem = result.timelineItem || null;

            // ✅ FERP SAFETY: Log successful operation with timeline details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'TimelineItem',
                    entityId: id,
                    found: !!timelineItem,
                    timelineType: timelineItem?.type,
                    timelineStatus: timelineItem?.status,
                    instanceId: timelineItem?.instanceId,
                    timestamp: Date.now()
                });
            }

            return timelineItem;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'TimelineItem',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(timelineItemId: string): Promise<TimelineItem | null> {
        return this.findByTenantAndId(this.tenant, timelineItemId);
    }

    // Timeline-specific methods

    /**
     * Get timeline items for a workflow instance
     * FERP SAFETY: Critical for audit trail visibility
     */
    async findByInstance(instanceId: string, options?: {
        type?: TimelineItem['type'];
        status?: TimelineItem['status'];
        limit?: number;
        orderBy?: 'timestamp_asc' | 'timestamp_desc';
    }): Promise<TimelineItem[]> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_INSTANCE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'TimelineItem',
            criticalityLevel: 'HIGH',
            metadata: { instanceId, options }
        });

        const query = `
            query GetTimelineItemsByInstance(
                $instanceId: ID!,
                $tenant: String!,
                $type: String,
                $status: String,
                $limit: Int,
                $orderBy: String
            ) {
                timelineItemsByInstance(
                    instanceId: $instanceId,
                    tenant: $tenant,
                    type: $type,
                    status: $status,
                    limit: $limit,
                    orderBy: $orderBy
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ timelineItemsByInstance: TimelineItem[] }>(query, {
                instanceId,
                tenant: this.tenant,
                type: options?.type,
                status: options?.status,
                limit: options?.limit,
                orderBy: options?.orderBy || 'timestamp_desc'
            }, {
                cache: true,
                cacheTime: 1 * 60 * 1000 // 1 minute cache for instance timelines
            });

            const items = result.timelineItemsByInstance || [];

            // ✅ FERP SAFETY: Log timeline retrieval
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_INSTANCE',
                    tenantId: this.tenant,
                    entityType: 'TimelineItem',
                    instanceId,
                    itemCount: items.length,
                    filterType: options?.type,
                    filterStatus: options?.status,
                    timestamp: Date.now()
                });
            }

            return items;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_INSTANCE',
                    tenantId: this.tenant,
                    entityType: 'TimelineItem',
                    instanceId,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    /**
     * Get recent timeline items across all instances
     * FERP SAFETY: Used for monitoring dashboards
     */
    async findRecent(limit?: number, type?: TimelineItem['type']): Promise<TimelineItem[]> {
        const query = `
            query GetRecentTimelineItems($limit: Int, $type: String, $tenant: String!) {
                recentTimelineItems(limit: $limit, type: $type, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            limit: limit || 50,
            type,
            tenant: this.tenant
        });

        return result.recentTimelineItems || [];
    }

    /**
     * Get timeline items by type
     * FERP SAFETY: Filter by step/signal/error/retry/transition
     */
    async findByType(type: TimelineItem['type'], limit?: number): Promise<TimelineItem[]> {
        const query = `
            query GetTimelineItemsByType($type: String!, $limit: Int, $tenant: String!) {
                timelineItemsByType(type: $type, limit: $limit, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            type,
            limit: limit || 100,
            tenant: this.tenant
        });

        return result.timelineItemsByType || [];
    }

    /**
     * Get error timeline items for debugging
     * FERP SAFETY: Critical for failure analysis
     */
    async findErrors(options?: {
        instanceId?: string;
        limit?: number;
        since?: string;
    }): Promise<TimelineItem[]> {
        const query = `
            query GetErrorTimelineItems(
                $instanceId: ID,
                $limit: Int,
                $since: String,
                $tenant: String!
            ) {
                errorTimelineItems(
                    instanceId: $instanceId,
                    limit: $limit,
                    since: $since,
                    tenant: $tenant
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            instanceId: options?.instanceId,
            limit: options?.limit || 50,
            since: options?.since,
            tenant: this.tenant
        });

        return result.errorTimelineItems || [];
    }

    /**
     * Get timeline statistics for an instance
     * FERP SAFETY: Provides metrics for life-critical workflow monitoring
     */
    async getInstanceStatistics(instanceId: string): Promise<{
        totalItems: number;
        completedCount: number;
        failedCount: number;
        pendingCount: number;
        typeBreakdown: Record<TimelineItem['type'], number>;
        firstEvent?: string;
        lastEvent?: string;
    }> {
        const query = `
            query GetTimelineStatistics($instanceId: ID!, $tenant: String!) {
                timelineStatistics(instanceId: $instanceId, tenant: $tenant) {
                    totalItems
                    completedCount
                    failedCount
                    pendingCount
                    typeBreakdown
                    firstEvent
                    lastEvent
                }
            }
        `;

        const result = await this.executeQuery(query, {
            instanceId,
            tenant: this.tenant
        });

        return result.timelineStatistics || {
            totalItems: 0,
            completedCount: 0,
            failedCount: 0,
            pendingCount: 0,
            typeBreakdown: {},
            firstEvent: undefined,
            lastEvent: undefined
        };
    }

    protected getFieldSelection(): string {
        return `
            id
            instanceId
            type
            title
            description
            timestamp
            data
            actor
            status
        `;
    }
}

export const timelineItemRepository = new TimelineItemRepository();
export { TimelineItemRepository };
